/**
 * MKS-164 — multi-select task label picker for TaskDetailSlideOver.
 *
 * Structural twin of TaskPriorityPicker.tsx (same useClickOutside + separate
 * Escape useEffect, same role="listbox" popover, same onError prop) but
 * MULTI-select: clicking an option toggles it and immediately calls
 * useSetTaskLabels with the WHOLE resulting id array (never a delta); the
 * popover stays open so several labels can be toggled in a row.
 *
 * Includes the inline create affordance the ticket allows: a name input +
 * "Create" button at the bottom of the popover, colour taken by cycling a
 * fixed 6-entry hex palette — no colour picker UI.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useProjectTaskLabels, useSetTaskLabels, useCreateProjectTaskLabel } from '../../hooks/useTaskLabels';
import { chipTextColor } from '../../utils/format';
import type { TaskLabel } from '../../services/api/board';

// Fixed 6-entry hex palette, cycled by the current option count — no colour
// picker UI (ticket scope). Distinct from MtPriorityBadge / tier palettes so
// a future dev doesn't merge them.
const CREATE_PALETTE = ['#2563EB', '#7C3AED', '#D97706', '#64748B', '#DC2626', '#059669'];

export interface TaskLabelPickerProps {
  taskId: number;
  projectId: number;
  currentLabels: TaskLabel[];
  canManage: boolean;
  onError?: (message: string) => void;
}

export function TaskLabelPicker({
  taskId,
  projectId,
  currentLabels,
  canManage,
  onError,
}: TaskLabelPickerProps) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const labelsQuery = useProjectTaskLabels(projectId);
  const setLabelsMutation = useSetTaskLabels();
  const createLabelMutation = useCreateProjectTaskLabel();

  useClickOutside(containerRef, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const currentIds = currentLabels.map((l) => l.id);

  const applyIds = useCallback(
    (labelIds: number[]) => {
      setLabelsMutation.mutate(
        { taskId, projectId: String(projectId), audience: 'internal', labelIds },
        {
          onError: (err) => {
            if (onError) onError(err.message ?? 'Server error');
          },
        },
      );
    },
    [setLabelsMutation, taskId, projectId, onError],
  );

  const toggleLabel = useCallback(
    (labelId: number) => {
      const next = currentIds.includes(labelId)
        ? currentIds.filter((id) => id !== labelId)
        : [...currentIds, labelId];
      applyIds(next);
    },
    [currentIds, applyIds],
  );

  const handleCreate = useCallback(() => {
    const name = newName.trim();
    if (!name) return;
    const options = labelsQuery.data ?? [];
    const color = CREATE_PALETTE[options.length % CREATE_PALETTE.length]!;
    createLabelMutation.mutate(
      { projectId, name, color },
      {
        onSuccess: (created) => {
          setNewName('');
          applyIds([...currentIds, created.id]);
        },
        onError: (err) => {
          if (onError) onError(err.message ?? 'Server error');
        },
      },
    );
  }, [newName, labelsQuery.data, createLabelMutation, projectId, currentIds, applyIds, onError]);

  // Read-only chips when the viewer has no rights to change labels.
  if (!canManage) {
    return (
      <span data-testid="task-label-picker">
        {currentLabels.map((label) => (
          <span
            key={label.id}
            style={{ backgroundColor: label.color, color: chipTextColor(label.color) }}
            className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none mr-1"
          >
            {label.name}
          </span>
        ))}
      </span>
    );
  }

  const fetchedOptions = labelsQuery.data ?? [];
  // MKS-406 — the project list can hide a label (switched off, or a machine label
  // the project does not use) that is still attached to this task. Keep it in the
  // popover as a selected, removable option so it is never invisible or silently
  // dropped by the whole-set save.
  const options = [
    ...fetchedOptions,
    ...currentLabels.filter((cl) => !fetchedOptions.some((o) => o.id === cl.id)),
  ];

  return (
    <div ref={containerRef} className="relative shrink-0" data-testid="task-label-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change labels"
        className="inline-flex items-center gap-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-400 hover:ring-2 hover:ring-ms-border"
        data-testid="task-label-picker-trigger"
      >
        {currentLabels.length === 0 ? (
          <span className="text-xs text-ms-text-secondary">Add labels</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {currentLabels.map((label) => (
              <span
                key={label.id}
                style={{ backgroundColor: label.color, color: chipTextColor(label.color) }}
                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none"
              >
                {label.name}
              </span>
            ))}
          </span>
        )}
        <ChevronDownIcon className="w-3.5 h-3.5 text-ms-text-muted" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Task label options"
          aria-multiselectable="true"
          className="absolute right-0 mt-1 w-56 rounded-lg border border-ms-border bg-white shadow-lg z-50 py-1"
          data-testid="task-label-picker-popover"
        >
          {options.map((label) => {
            const selected = currentIds.includes(label.id);
            return (
              <button
                key={label.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => toggleLabel(label.id)}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-ms-canvas focus:bg-ms-canvas focus:outline-none"
                data-testid={`task-label-option-${label.id}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: label.color }}
                  />
                  <span className="text-sm text-ms-text truncate">{label.name}</span>
                </span>
                {selected && (
                  <CheckIcon className="w-4 h-4 text-ms-text-secondary shrink-0" aria-hidden="true" />
                )}
              </button>
            );
          })}

          <div className="flex items-center gap-1 px-3 py-1.5 border-t border-ms-border-subtle mt-1">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New label"
              data-testid="task-label-create-input"
              className="flex-1 min-w-0 rounded border border-ms-border px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={newName.trim().length === 0 || createLabelMutation.isPending}
              data-testid="task-label-create-button"
              className="inline-flex items-center rounded p-1 text-ms-text-secondary hover:bg-ms-canvas disabled:opacity-50"
              title="Create label"
            >
              <PlusIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
